import { ColDef, ColGroupDef, ValueGetterParams } from "ag-grid-enterprise";
import { DateFilter, DateUtils } from "utils/dateutils";
import { FeatureTypes } from "entity-app/constants";
import EntityDropdownCellEditor from "components/cell-editor/drop-down-editor";
import { TaskLinksCellRenderer } from "components/cell-renders";
import { getValueFromDotPath } from "entity-app/utils/utils";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { LINKED_ACTIVITIES_COLUMN_FILTER_LABELS } from "../../../constants";
import {
  dateTimeTwoLineCellRenderer,
  linkCellRenderer,
  userEntityCellRenderer
} from "./cell-renders";

export const dateTimeAuditColumnOverrides = (
  field: "last_manual_updated_at" | "last_system_updated_at"
) => ({
  editable: false,
  initialWidth: 200,
  cellRenderer: dateTimeTwoLineCellRenderer,
  filter: CustomDateSetFilter,
  valueGetter: (params: ValueGetterParams) => params.data?.[field] || undefined,
  comparator: (
    valueA: string | null,
    valueB: string | null,
    nodeA?: any,
    nodeB?: any,
    isInverted?: boolean
  ) =>
    DateFilter.dateTimeComparator(
      nodeA?.data?.[field] || valueA,
      nodeB?.data?.[field] || valueB,
      nodeA,
      nodeB,
      isInverted
    )
});

export const coldefDateField = (def: ColDef<any> | ColGroupDef<any>) => {
  if ("field" in def) {
    const { field, headerName } = def;
    if (!field) return {} as ColDef<any>;
    return {
      colId: field,
      field,
      headerName,
      headerTooltip: headerName,
      cellDataType: "dateString",
      valueGetter: ({ data }: any) => {
        const value = getValueFromDotPath(data, field);
        return value ? DateUtils.format(value) : "";
      },
      // Prefer raw row data over formatted MM-DD-YYYY cell values. Safari's
      // native Date.parse rejects MM-DD-YYYY; sorting ISO/calendar source dates
      // (with DateFilter's multi-format parser) keeps order correct on all browsers.
      comparator: (
        valueA: string | null,
        valueB: string | null,
        nodeA?: any,
        nodeB?: any,
        isInverted?: boolean
      ) => {
        const rawA =
          nodeA?.data != null
            ? getValueFromDotPath(nodeA.data, field) || valueA
            : valueA;
        const rawB =
          nodeB?.data != null
            ? getValueFromDotPath(nodeB.data, field) || valueB
            : valueB;
        return DateFilter.comparator(
          rawA || null,
          rawB || null,
          nodeA,
          nodeB,
          isInverted
        );
      },
      filter: CustomDateSetFilter,
      cellClass: (params) => {
        const isCellEditble = params?.context?.isGridCellEditable;
        return isCellEditble && isCellEditble(params) ? "cell-editable" : "";
      },
      ...def
    } as ColDef<any>;
  }
  return { ...def } as ColGroupDef<any>;
};

export const coldefHiddenField = (def: ColDef<any>) => {
  const { field, headerName } = def;
  return {
    colId: field,
    field,
    headerName,
    editable: false,
    hide: true,
    suppressColumnsToolPanel: true,
    filter: false,
    getQuickFilterText: () => "",
    ...def
  } as ColDef<any> | ColGroupDef<any>;
};

export const coldefSingleField = (def: ColDef<any> | ColGroupDef<any>) => {
  if ("field" in def && "headerName" in def) {
    const { field, headerName } = def;
    return {
      colId: field,
      field,
      headerName,
      headerTooltip: headerName,
      cellClass: (params) => {
        const isCellEditble = params?.context?.isGridCellEditable;
        return isCellEditble && isCellEditble(params) ? "cell-editable" : "";
      },
      ...def
    } as ColDef<any>;
  }
  return { ...def } as ColGroupDef<any>;
};

export const coldefNavigation = (
  def: ColDef<any>,
  projectId: string,
  featureKey: string
) => {
  return {
    cellRenderer: linkCellRenderer,
    cellRendererParams: {
      projectId,
      featureKey
    },
    ...coldefSingleField(def)
  } as ColDef<any> | ColGroupDef<any>;
};

export const coldefUser = ({
  user,
  def
}: {
  def: ColDef<any>;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    statusId?: string;
    unregistered?: string;
    dropdownType?: string;
    featureTypeId?: FeatureTypes;
    dependentId?: string;
    dependentNotFoundContent?: string;
    companyObjField?: string;
    participantsSource?: string;
  };
}) => {
  const { headerName } = def;
  return {
    colId: user.id,
    headerName,
    headerTooltip: headerName,
    valueGetter: ({ data }) => {
      if (user.unregistered) {
        const unregistered = getValueFromDotPath(data, user.unregistered);
        if (unregistered) return unregistered;
      }
      const id = getValueFromDotPath(data, user.id);
      if (!id) return "";
      const firstName = getValueFromDotPath(data, user.firstName) || "";
      const lastName = getValueFromDotPath(data, user.lastName);
      if (user.companyObjField) {
        const companyObj = getValueFromDotPath(data, user.companyObjField);
        if (companyObj)
          return lastName
            ? `${firstName} ${lastName} ( ${companyObj.name} )`
            : `${firstName} ( ${companyObj.name} )`;
      }
      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    valueFormatter: ({ data }) => {
      if (user.unregistered) {
        const unregistered = getValueFromDotPath(data, user.unregistered);
        if (unregistered) return unregistered;
      }
      const id = getValueFromDotPath(data, user.id);
      if (!id) return "";
      const firstName = getValueFromDotPath(data, user.firstName) || "";
      const lastName = getValueFromDotPath(data, user.lastName);

      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    cellRenderer: userEntityCellRenderer,
    cellRendererParams: {
      statusId: user.statusId,
      companyObjField: user.companyObjField
    },
    cellEditor: EntityDropdownCellEditor,
    cellEditorParams: {
      user
    },
    filter: "agSetColumnFilter",
    cellClass: (params) => {
      const isCellEditble = params?.context?.isGridCellEditable;
      return isCellEditble && isCellEditble(params) ? "cell-editable" : "";
    },
    ...def
  } as ColDef<any> | ColGroupDef<any>;
};

export const coldefUserV2 = ({
  user,
  def
}: {
  def: ColDef<any>;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    statusId?: string;
    dropdownType?: string;
    featureTypeId?: FeatureTypes;
    dependentId?: string;
    dependentNotFoundContent?: string;
    companyObjField?: string;
    participantsSource?: string;
  };
}) => {
  const { headerName } = def;
  return {
    colId: user.id,
    headerName,
    headerTooltip: headerName,
    valueGetter: ({ data }) => {
      const id = getValueFromDotPath(data, user.id);
      if (!id) return "";
      const firstName = getValueFromDotPath(data, user.firstName) || "";
      const lastName = getValueFromDotPath(data, user.lastName);
      if (user.companyObjField) {
        const companyObj = getValueFromDotPath(data, user.companyObjField);
        if (companyObj)
          return lastName ? `${firstName} ${lastName}` : `${firstName}`;
      }
      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    filterValueGetter: ({ data }) => {
      const id = getValueFromDotPath(data, user.id);
      if (!id) return "";
      const firstName = getValueFromDotPath(data, user.firstName) || "";
      const lastName = getValueFromDotPath(data, user.lastName);
      if (user.companyObjField) {
        const companyObj = getValueFromDotPath(data, user.companyObjField);
        if (companyObj)
          return lastName
            ? `${firstName} ${lastName} ( ${companyObj.name} )`
            : `${firstName} ( ${companyObj.name} )`;
      }
      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    valueFormatter: ({ data }) => {
      const id = getValueFromDotPath(data, user.id);
      if (!id) return "";
      const firstName = getValueFromDotPath(data, user.firstName) || "";
      const lastName = getValueFromDotPath(data, user.lastName);

      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    cellRenderer: userEntityCellRenderer,
    cellRendererParams: {
      statusId: user.statusId,
      companyObjField: user.companyObjField
    },
    cellEditor: EntityDropdownCellEditor,
    cellEditorParams: {
      user
    },
    filter: "agSetColumnFilter",
    cellClass: (params) => {
      const isCellEditble = params?.context?.isGridCellEditable;
      return isCellEditble && isCellEditble(params) ? "cell-editable" : "";
    },
    ...def
  } as ColDef<any> | ColGroupDef<any>;
};

const getCriticalityLabel = (value: any): string => {
  if (!value) {
    return LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.NO_ACTIVITIES_LINKED;
  }
  if (value?.deleted_from_source) {
    return LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.DELETED;
  }
  return value.critical
    ? LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.CRITICAL
    : LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.NOT_CRITICAL;
};

const getCriticalityPath = (value: any): string[] =>
  value && !value?.deleted_from_source
    ? [
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.ACTIVITY_LINKED,
        getCriticalityLabel(value)
      ]
    : value?.deleted_from_source
      ? [LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.DELETED]
      : [LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.NO_ACTIVITIES_LINKED];

export const coldefLinkedActivities = (def: ColDef<any>) => {
  return {
    cellRenderer: TaskLinksCellRenderer,
    valueGetter: ({ data }: any) => {
      if (!data?.tasks?.length) return [];
      return data?.tasks?.map((activity: any) => {
        const name = activity.activity_id
          ? `${activity.activity_id} - ${activity.task_name}`
          : activity.task_name;
        return { ...activity, name };
      });
    },
    filterParams: {
      treeList: true,
      treeListPathGetter: getCriticalityPath,
      keyCreator: ({ value }: any) => getCriticalityLabel(value)
    },
    width: 420,
    minWidth: 420,
    resizable: false,
    ...def,
    colId: def.colId || def.field
  } as ColDef<any> | ColGroupDef<any>;
};
